import React, {
  useCallback, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { Box } from '@mui/system';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import CheckIcon from '@mui/icons-material/Check';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PublicOffIcon from '@mui/icons-material/PublicOff';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

import {
  authorColor, authorInitials, diffAuthorAgainstPublic, getAuthorImageUrl, getAuthorName,
  getPrimaryLinkUrl, mergeAuthor, shareActionTitle, shareChangesState, shareResultMessage,
} from '../../common/ItemTools';
import { DELETE_LABEL, UNSHARE_LABEL } from '../../common/ActionLabels';
import {
  bookmarkAuthor, deleteAuthor, replaceAuthor, shareAuthor, updateAuthor,
} from '../../redux/features/authors/authorsSlice';
import { bookmarkItem, fetchItemsByAuthorPreview, shareItem } from '../../redux/features/items/itemsSlice';
import { setSnackbar } from '../../redux/features/snackbarSlice';
import { getNamespacePath } from '../../redux/features/context/contextSlice';
import useConfirm from '../../hooks/useConfirm';
import useGoBack from '../../hooks/useGoBack';
import FitItemGrid from '../FitItemGrid';
import {
  AccentButton, ActionDivider, BackButton, DangerButton, PageBackground, HeroCard, SectionLabel, OutlinedButton, PrimaryButton,
} from '../common/hero/styles';
import HeroIdentityHeader, { HeroAvatar } from '../common/hero/IdentityHeader';
import HeroFacts from '../common/hero/Facts';
import ViewAllLink from '../common/hero/ViewAllLink';
import BookmarkToggle from '../common/hero/BookmarkToggle';
import FavoriteButton from '../common/hero/FavoriteButton';
import ScopeControl from '../common/hero/ScopeControl';
import ShareButton from '../common/hero/ShareButton';
import HeroVerdictActions from '../common/hero/VerdictActions';
import useVerdict from '../common/hero/useVerdict';
import { factsLine, openUrlInNewTab } from '../common/hero/utils';
import { useFitGrid } from '../common/hero/useFitGrid';
import { useNamespacePreviews } from '../common/hero/useNamespacePreviews';
import PlatformIcon from './PlatformIcon';
import ProfilesEditor from './ProfilesEditor';
import {
  cleanProfile, deriveHandle, deriveName, derivePortraitUrl,
  isEmptyProfile, urlBadges,
} from './utils';

// Badge logo sizes per platform (the brand marks have different visual
// weights, so the boxes differ to look optically equal).
const BADGE_SIZES = {
  github: 20,
  x: 16,
  youtube: 22,
  linkedin: 19,
  web: 19,
  hackernews: 19,
  lobsters: 19,
  wikipedia: 22,
  reddit: 20,
};

// Editable snapshot of an author's profiles (always at least one row so the
// editor has something to show).
function buildDraft(merged) {
  const profiles = (merged?.profiles || []).map((p) => ({ ...p }));
  return { profiles: profiles.length ? profiles : [{ name: '' }] };
}

export default function AuthorHeroPage(props) {
  const {
    author: rawAuthor, namespace, onRefetch, startInEdit,
  } = props;

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const goBack = useGoBack();
  const { confirm, confirmDialog } = useConfirm();
  const userInfo = useSelector((state) => state.users.userInfo);

  const merged = useMemo(() => mergeAuthor(rawAuthor), [rawAuthor]);
  const shareDiff = useMemo(() => diffAuthorAgainstPublic(rawAuthor), [rawAuthor]);

  const [editing, setEditing] = useState(Boolean(startInEdit));
  const [draft, setDraft] = useState(() => (startInEdit ? buildDraft(merged) : null));
  const [saving, setSaving] = useState(false);

  const isPending = namespace === 'pending';
  const editNamespace = isPending ? 'pending' : 'private';
  const scoped = Boolean(userInfo) && !isPending;

  const { decide, deciding } = useVerdict('author', rawAuthor.id, getAuthorName(rawAuthor));

  // Measure the space left under the identity header and fill it with square
  // cards sized to the section's real dimensions.
  const gridAreaRef = useRef(null);
  const { arrangement: gridArrangement, pageSize } = useFitGrid(gridAreaRef, { deps: [editing] });

  const fetchPreview = useCallback((ns, limit) => dispatch(fetchItemsByAuthorPreview({
    namespace: ns, authorId: rawAuthor.id, params: { limit },
  })).unwrap(), [dispatch, rawAuthor.id]);

  const {
    previews, loaded: previewsLoaded, activeNamespace, setScope,
    showScope, scopeOptions, recent, refresh: refreshPreviews,
  } = useNamespacePreviews({
    entityId: rawAuthor.id, namespace, scoped, pageSize, fetchPreview,
  });

  // Edit lifecycle
  const enterEdit = () => {
    setDraft(buildDraft(merged));
    setEditing(true);
  };
  const cancelEdit = () => {
    setDraft(null);
    setEditing(false);
  };

  const share = () => {
    dispatch(shareAuthor({ authorId: rawAuthor.id }))
      .unwrap()
      .then((result) => {
        dispatch(setSnackbar({
          snackbarOpen: true,
          snackbarType: 'success',
          snackbarMessage: shareResultMessage(result),
        }));
        if (onRefetch) onRefetch();
      })
      .catch(() => dispatch(setSnackbar({
        snackbarOpen: true, snackbarType: 'error', snackbarMessage: 'Failed to share the author.',
      })));
  };

  const toggleBookmark = async () => {
    if (rawAuthor.privateData) {
      const ok = await confirm({
        title: 'Remove from your library?',
        message: `Your private copy of "${getAuthorName(rawAuthor) || 'this author'}" is deleted, along with any changes you made to it. The public author stays.`,
        confirmLabel: 'Remove',
        destructive: true,
      });
      if (!ok) return;
      dispatch(deleteAuthor({ namespace: 'private', authorId: rawAuthor.id }))
        .unwrap()
        .then(() => onRefetch && onRefetch())
        .catch(() => dispatch(setSnackbar({
          snackbarOpen: true, snackbarType: 'error', snackbarMessage: 'Failed to remove the private copy.',
        })));
      return;
    }

    dispatch(bookmarkAuthor({ authorId: rawAuthor.id }))
      .unwrap()
      .then(() => onRefetch && onRefetch())
      .catch(() => dispatch(setSnackbar({
        snackbarOpen: true, snackbarType: 'error', snackbarMessage: 'Failed to bookmark the author.',
      })));
  };

  const removeAuthor = async () => {
    const ok = await confirm({
      title: 'Delete author?',
      message: `"${getAuthorName(rawAuthor) || 'This author'}" will be permanently removed from your library.`,
      confirmLabel: DELETE_LABEL,
      destructive: true,
    });
    if (!ok) return;
    dispatch(deleteAuthor({ namespace: 'private', authorId: rawAuthor.id }))
      .unwrap()
      .then(goBack)
      .catch(() => dispatch(setSnackbar({
        snackbarOpen: true, snackbarType: 'error', snackbarMessage: 'Failed to delete the author.',
      })));
  };

  const unshareAuthor = async () => {
    const ok = await confirm({
      title: 'Unshare author?',
      message: `"${getAuthorName(rawAuthor) || 'This author'}" will be removed from the public record. Its creator keeps their private copy.`,
      confirmLabel: UNSHARE_LABEL,
      destructive: true,
    });
    if (!ok) return;
    dispatch(deleteAuthor({ namespace: 'public', authorId: rawAuthor.id }))
      .unwrap()
      .then(() => {
        if (rawAuthor.privateData) {
          if (onRefetch) onRefetch();
        } else {
          goBack();
        }
      })
      .catch(() => dispatch(setSnackbar({
        snackbarOpen: true, snackbarType: 'error', snackbarMessage: 'Failed to unshare the author.',
      })));
  };

  // Favorite persists immediately.
  const toggleFavorite = () => {
    dispatch(updateAuthor({
      namespace: 'private', authorId: rawAuthor.id, patch: { isFavorite: !merged?.isFavorite },
    }))
      .unwrap()
      .then(() => onRefetch && onRefetch())
      .catch(() => dispatch(setSnackbar({
        snackbarOpen: true, snackbarType: 'error', snackbarMessage: 'Failed to favorite the author.',
      })));
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const profiles = draft.profiles.map(cleanProfile).filter((p) => !isEmptyProfile(p));
      await dispatch(replaceAuthor({
        namespace: editNamespace,
        authorId: rawAuthor.id,
        author: { id: rawAuthor.id, profiles },
      })).unwrap();
      setEditing(false);
      setDraft(null);
      if (onRefetch) onRefetch();
    } catch (error) {
      dispatch(setSnackbar({
        snackbarOpen: true,
        snackbarType: 'error',
        snackbarMessage: 'Failed to save the author.',
      }));
    } finally {
      setSaving(false);
    }
  };

  const setProfile = (idx, field, value) => setDraft((d) => ({
    profiles: d.profiles.map((p, i) => (i === idx ? { ...p, [field]: value } : p)),
  }));
  const addProfile = () => setDraft((d) => ({ profiles: [...d.profiles, { name: '' }] }));
  const removeProfile = (idx) => setDraft((d) => ({
    profiles: d.profiles.filter((_, i) => i !== idx),
  }));
  const moveProfile = (from, to) => setDraft((d) => {
    const profiles = [...d.profiles];
    const [moved] = profiles.splice(from, 1);
    profiles.splice(to, 0, moved);
    return { profiles };
  });

  const shareItemFromCard = (it) => dispatch(shareItem({ itemId: it.id }))
    .unwrap()
    .then((result) => {
      dispatch(setSnackbar({
        snackbarOpen: true, snackbarType: 'success', snackbarMessage: shareResultMessage(result),
      }));
      return refreshPreviews();
    })
    .catch(() => dispatch(setSnackbar({
      snackbarOpen: true, snackbarType: 'error', snackbarMessage: 'Failed to share the item.',
    })));

  const bookmarkItemFromCard = (it) => dispatch(bookmarkItem({ itemId: it.id }))
    .unwrap()
    .then(refreshPreviews)
    .catch(() => dispatch(setSnackbar({
      snackbarOpen: true, snackbarType: 'error', snackbarMessage: 'Failed to bookmark item.',
    })));

  const namespacePath = getNamespacePath(activeNamespace);

  const itemCardActions = {
    onItemRead: (it) => navigate(isPending ? `/moderate/items/${it.id}` : `/items/${it.id}`),
    onItemOpen: (it) => openUrlInNewTab(getPrimaryLinkUrl(it)),
    onTagOpen: (tagId) => navigate(`/${namespacePath}/items?tag=${tagId}`),
    onAuthorView: (authorId) => navigate(`/authors/${authorId}`),
    onAuthorFilter: (authorId) => navigate(`/${namespacePath}/items?author=${authorId}`),
    onSiteView: (siteId) => navigate(`/sites/${siteId}`),
    onSiteFilter: (siteId) => navigate(`/${namespacePath}/items?site=${siteId}`),
    ...(scoped && {
      onItemShare: shareItemFromCard,
      onItemBookmark: bookmarkItemFromCard,
    }),
  };

  // Derived identity (name/portrait/handle come from the profiles, so in
  // edit mode the header re-derives live from the draft)
  const profiles = editing && draft ? draft.profiles : (merged?.profiles || []);
  const name = deriveName(profiles);
  const handle = deriveHandle(profiles);
  const portraitUrl = editing && draft
    ? derivePortraitUrl(profiles)
    : (merged && getAuthorImageUrl(merged, merged.authorImageScope));
  const badges = urlBadges(profiles);
  const countsPending = !editing && !previewsLoaded;
  const hasPrivate = Boolean(rawAuthor.privateData);
  const hasPublic = Boolean(rawAuthor.publicData);
  // Only the namespace on show is quoted — the scope control beside the line
  // is what switches it.
  const activeData = isPending ? merged : rawAuthor[`${activeNamespace}Data`];
  const facts = factsLine(activeData, previews[activeNamespace]?.itemsCount, activeNamespace);

  const canBookmark = scoped && hasPublic;
  const canShare = scoped && hasPrivate;
  const shareChanges = shareChangesState(shareDiff);
  const canUnshare = !isPending && hasPublic && Boolean(userInfo?.isModerator);
  const canDelete = !isPending && hasPrivate && !hasPublic;
  const canDecide = isPending && Boolean(userInfo?.isModerator);

  const readActions = !editing && userInfo ? (
    <>
      {!isPending && (
        <FavoriteButton
          isFavorite={Boolean(merged?.isFavorite)}
          onToggle={toggleFavorite}
        />
      )}
      {canBookmark && (
        <BookmarkToggle hasPrivateData={hasPrivate} onToggle={toggleBookmark} />
      )}
      {canShare && (
        <ShareButton
          hasPublicData={hasPublic}
          changes={shareChanges}
          title={shareActionTitle(hasPublic, shareDiff)}
          onShare={share}
        />
      )}
      {!isPending && <ActionDivider />}
      {canUnshare && (
        <DangerButton type="button" onClick={unshareAuthor}>
          <PublicOffIcon sx={{ fontSize: 16 }} />
          {UNSHARE_LABEL}
        </DangerButton>
      )}
      {canDelete && (
        <DangerButton type="button" onClick={removeAuthor}>
          <DeleteOutlineIcon sx={{ fontSize: 16 }} />
          {DELETE_LABEL}
        </DangerButton>
      )}
      <AccentButton type="button" onClick={enterEdit}>
        <EditOutlinedIcon sx={{ fontSize: 16 }} />
        Edit
      </AccentButton>
      {canDecide && (
        <>
          <ActionDivider />
          <HeroVerdictActions onDecide={decide} disabled={deciding} />
        </>
      )}
    </>
  ) : null;
  const headerActions = editing ? (
    <>
      <OutlinedButton type="button" onClick={cancelEdit} disabled={saving}>Cancel</OutlinedButton>
      <PrimaryButton type="button" onClick={saveEdit} disabled={saving}>
        <CheckIcon sx={{ fontSize: 16 }} />
        Save
      </PrimaryButton>
    </>
  ) : readActions;

  // Never render more than the current page holds, so a shrink-resize can't
  // briefly overflow the grid with stale items before the refetch lands.
  const displayItems = pageSize ? recent.items.slice(0, pageSize) : recent.items;
  // Items that fit in one row fill it: cards widen to one column each (4
  // columns max via the measured grid). A lone item is sized as if there
  // were two columns, so it takes half the width rather than all of it.
  const itemCount = displayItems.length;
  const displayArrangement = gridArrangement && itemCount > 0 && itemCount <= gridArrangement.cols
    ? { cols: Math.max(2, itemCount), rows: 1 }
    : gridArrangement;

  return (
    <PageBackground sx={{ padding: 0 }}>
      {/* Explicit 100% height keeps the whole chain down to the card grid
          definite, which the container-query card sizing requires (a
          min-height-derived height collapses it). */}
      <HeroCard sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          padding: '16px 28px 0',
          width: '100%',
          maxWidth: 1400,
          mx: 'auto',
          flexShrink: 0,
        }}>
          <BackButton type="button" onClick={goBack}>
            <ChevronLeftIcon sx={{ fontSize: 18 }} />
            <span>Back</span>
          </BackButton>
          <Box sx={{ flex: 1 }} />
          {headerActions && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {headerActions}
            </Box>
          )}
        </Box>

        {/* Content column — scrolls internally when the content doesn't fit
            (e.g. a long profiles editor), keeping the action bar pinned */}
        <Box sx={{
          width: '100%',
          maxWidth: 1232,
          mx: 'auto',
          padding: '24px 28px 24px',
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto',
        }}>
          {/* Identity header (identical in both modes) */}
          <HeroIdentityHeader
            avatar={(
              <HeroAvatar imageUrl={portraitUrl} fit="cover" background={authorColor(name)}>
                {authorInitials(name)}
              </HeroAvatar>
            )}
            title={name}
            subtitle={handle && handle !== name ? handle : null}
            links={badges.length > 0 ? badges.map((badge) => (
              <Box
                key={badge.url}
                role="button"
                title={badge.tip}
                onClick={() => openUrlInNewTab(badge.url)}
                sx={{
                  display: 'flex',
                  cursor: 'pointer',
                  '&:hover': { opacity: 0.6 },
                }}
              >
                <PlatformIcon
                  platform={badge.platform}
                  size={BADGE_SIZES[badge.platform] || BADGE_SIZES.web}
                />
              </Box>
            )) : null}
          />

          {/* Profiles editor (edit mode) */}
          {editing && draft && (
            <ProfilesEditor
              profiles={draft.profiles}
              setProfile={setProfile}
              addProfile={addProfile}
              removeProfile={removeProfile}
              moveProfile={moveProfile}
            />
          )}

          {/* Recent items (read mode): fills the rest of the page height,
              cards fit the space like the item views. The wrapper renders
              (and is measured) before the items arrive — the measurement
              decides how many items to fetch. */}
          {!editing && (
            <Box
              ref={gridAreaRef}
              sx={{
                flex: 1,
                minHeight: 240,
                display: 'flex',
                flexDirection: 'column',
                mt: '18px',
              }}
            >
              {displayItems.length > 0 && (
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  minHeight: 30,
                  gap: '14px',
                  flexShrink: 0,
                }}>
                  <SectionLabel sx={{ mb: 0 }}>Recent items</SectionLabel>
                  <Box sx={{
                    display: 'flex', alignItems: 'center', gap: '14px', minWidth: 0,
                  }}>
                    <HeroFacts facts={facts} loading={countsPending} />
                    {showScope && (
                      <ScopeControl
                        options={scopeOptions}
                        value={activeNamespace}
                        onChange={setScope}
                      />
                    )}
                    <ViewAllLink
                      title={`View all ${recent.itemsCount} items by ${name}`}
                      onClick={() => navigate(`/${namespacePath}/items?author=${rawAuthor.id}`)}
                    />
                  </Box>
                </Box>
              )}
              {displayItems.length > 0 && (
                <FitItemGrid
                  items={displayItems}
                  namespace={activeNamespace}
                  itemActions={itemCardActions}
                  arrangement={displayArrangement}
                  isPreview
                />
              )}
            </Box>
          )}
        </Box>
      </HeroCard>

      {confirmDialog}
    </PageBackground>
  );
}

AuthorHeroPage.propTypes = {
  author: PropTypes.object.isRequired,
  namespace: PropTypes.string,
  onRefetch: PropTypes.func,
  startInEdit: PropTypes.bool,
};

AuthorHeroPage.defaultProps = {
  namespace: 'public',
  onRefetch: null,
  startInEdit: false,
};
